import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { useHistory, useLocation } from "react-router-dom";
import { PageHeaderWithBack } from "../../../Components/Molecules";
import { useAuth } from "../../../Contexts/AuthContext";
import { CONTA_INTERNACIONAL_HOME_PATH } from "../ContaInternacional/constants";
import { CrossborderPayinModal } from "./CrossborderPayinModal";
import { CrossborderPayoutModal } from "./CrossborderPayoutModal";
import { CrossborderPayoutVolumeCard } from "./components/CrossborderPayoutVolumeCard";
import { CrossborderHistoryTable } from "./components/CrossborderHistoryTable";
import { CrossborderShortcutMenu } from "./components/CrossborderShortcutMenu";
import { CrossborderTransactionDetailDrawer } from "./components/CrossborderTransactionDetailDrawer";
import {
  CROSSBORDER_CUSTOMERS_PATH,
  CROSSBORDER_HOME_PATH,
  MOCK_HISTORY,
} from "./constants";

const clearModalSearchParam = (history, location) => {
  const params = new URLSearchParams(location.search);
  if (!params.has("modal")) {
    return;
  }

  params.delete("modal");
  history.replace({
    pathname: location.pathname,
    search: params.toString() ? `?${params.toString()}` : "",
  });
};

/**
 * IB Crossborder home. Also reused by BaaS Console with admin props
 * (account filter slot, custom header, path overrides).
 *
 * @param {object} [props]
 * @param {boolean} [props.isBaasAdmin]
 * @param {boolean} [props.hideHeader]
 * @param {Array} [props.historyItems]
 * @param {import("react").ReactNode} [props.filterSlot]
 * @param {string} [props.homePath]
 * @param {string} [props.customersPath]
 * @param {boolean} [props.showCustomers]
 * @param {string} [props.backTo]
 * @param {boolean} [props.canMutate] Optional override; defaults to `!isReadOnly` from auth.
 */
export const Crossborder = ({
  isBaasAdmin = false,
  hideHeader = false,
  historyItems = MOCK_HISTORY,
  filterSlot = null,
  homePath = CROSSBORDER_HOME_PATH,
  customersPath = CROSSBORDER_CUSTOMERS_PATH,
  showCustomers = true,
  backTo = CONTA_INTERNACIONAL_HOME_PATH,
  canMutate: canMutateProp = undefined,
} = {}) => {
  const history = useHistory();
  const location = useLocation();
  const { isReadOnly } = useAuth();
  const canMutate = canMutateProp ?? !isReadOnly;
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [payinOpen, setPayinOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);

  useEffect(() => {
    const modal = new URLSearchParams(location.search).get("modal");
    if (!canMutate) {
      setPayinOpen(false);
      setPayoutOpen(false);
      return;
    }
    setPayinOpen(modal === "payin");
    setPayoutOpen(modal === "payout");
  }, [canMutate, location.search]);

  const handleShowDetail = useCallback((item) => {
    setSelectedTransaction(item);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedTransaction(null);
  }, []);

  const handleOpenPayin = useCallback(() => {
    setPayinOpen(true);
  }, []);

  const handleOpenPayout = useCallback(() => {
    setPayoutOpen(true);
  }, []);

  const handleClosePayin = useCallback(() => {
    setPayinOpen(false);
    clearModalSearchParam(history, location);
  }, [history, location]);

  const handleClosePayout = useCallback(() => {
    setPayoutOpen(false);
    clearModalSearchParam(history, location);
  }, [history, location]);

  return (
    <div className="w-full">
      {!hideHeader ? (
        <PageHeaderWithBack
          title={intl.get("crossborder.title")}
          subTitle={intl.get("crossborder.subtitle")}
          backTo={isBaasAdmin ? undefined : backTo}
        />
      ) : null}

      <CrossborderShortcutMenu
        onOpenPayin={handleOpenPayin}
        onOpenPayout={handleOpenPayout}
        homePath={homePath}
        customersPath={customersPath}
        showCustomers={showCustomers}
        canMutate={canMutate}
      />

      <CrossborderPayoutVolumeCard className="mb-5" />

      {filterSlot}

      <section>
        <h2 className="mb-1 text-sm font-bold text-brand-dark dark:text-gray-100">
          {intl.get("crossborder.history")}
        </h2>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("crossborder.historyHint")}
        </p>
        <CrossborderHistoryTable items={historyItems} onShowDetail={handleShowDetail} />
      </section>

      <CrossborderTransactionDetailDrawer
        transaction={selectedTransaction}
        onClose={handleCloseDetail}
      />

      {canMutate ? (
        <CrossborderPayinModal open={payinOpen} onClose={handleClosePayin} />
      ) : null}
      {canMutate ? (
        <CrossborderPayoutModal open={payoutOpen} onClose={handleClosePayout} />
      ) : null}
    </div>
  );
};

export { CrossborderCustomers } from "./CrossborderCustomers";
export { CrossborderCustomerProfile } from "./CrossborderCustomerProfile";
